// Single source of truth for website roles. Pure (no database or server imports) so both
// API routes and client components can use it.
//
// Roles are ranked lowest -> highest. Every permission check is a rank comparison against
// one of the tier floors below, so adding a role only means slotting it into ROLE_ORDER.

export const ROLE_ORDER = [
  'default',
  'linked',
  'friend',
  'booster',
  'staff',
  'developer',
  'admin',
  'head_of_support',
  'manager_muff',
  'owner',
];

export const ROLE_LABELS = {
  default: 'Default',
  linked: 'Linked',
  friend: 'Friend',
  booster: 'Booster',
  staff: 'Staff',
  developer: 'Developer',
  admin: 'Admin',
  head_of_support: 'Head of Support',
  manager_muff: 'Manager Muff',
  owner: 'Owner',
};

// Lowest role in each permission tier.
// - Staff tier: tickets, site help, posts.
// - Admin tier: the admin panel and everything that used to require the 'admin' role.
// - Owner tier: Manager Muff and Owner are co-owners. They bypass the rank rule for
//   managing users, so they can assign any role (including each other's).
export const STAFF_TIER_FLOOR = 'staff';
export const ADMIN_TIER_FLOOR = 'developer';
export const OWNER_TIER_FLOOR = 'manager_muff';
// Admin and above never see ads. Lower roles only go ad-free with a paid server or when
// an admin turns ads off for their account.
export const AD_FREE_FLOOR = 'admin';

export function normalizeRole(role) {
  const value = String(role || '').trim().toLowerCase();
  return ROLE_ORDER.includes(value) ? value : 'default';
}

export function isValidRole(role) {
  return ROLE_ORDER.includes(String(role || ''));
}

export function roleRank(role) {
  return ROLE_ORDER.indexOf(normalizeRole(role));
}

export function roleAtLeast(role, floor) {
  return roleRank(role) >= roleRank(floor);
}

export function isStaffRole(role) {
  return roleAtLeast(role, STAFF_TIER_FLOOR);
}

export function isAdminRole(role) {
  return roleAtLeast(role, ADMIN_TIER_FLOOR);
}

export function isOwnerTierRole(role) {
  return roleAtLeast(role, OWNER_TIER_FLOOR);
}

export function isAdFreeRole(role) {
  return roleAtLeast(role, AD_FREE_FLOOR);
}

export function getRoleLabel(role) {
  return ROLE_LABELS[normalizeRole(role)];
}

// Can someone with `actorRole` change a user whose current role is `targetRole`?
// Owner tier can manage anyone. Other admins can only manage users strictly below them.
export function canManageUser(actorRole, targetRole) {
  if (isOwnerTierRole(actorRole)) return true;
  if (!isAdminRole(actorRole)) return false;
  return roleRank(targetRole) < roleRank(actorRole);
}

// Roles `actorRole` may hand out. Owner tier: every role. Other admins: only roles
// strictly below their own (so Head of Support can assign Admin, but not Head of Support).
export function assignableRoles(actorRole) {
  if (isOwnerTierRole(actorRole)) return [...ROLE_ORDER];
  if (!isAdminRole(actorRole)) return [];
  return ROLE_ORDER.filter((role) => roleRank(role) < roleRank(actorRole));
}

export function canAssignRole(actorRole, targetCurrentRole, newRole) {
  if (!isValidRole(newRole)) return false;
  return canManageUser(actorRole, targetCurrentRole) && assignableRoles(actorRole).includes(newRole);
}

// Looks up a per-role setting (e.g. free server limits). A role without its own entry
// inherits the value of the nearest role below it, so new roles never fall back to the
// lowest default by accident.
export function valueForRole(map, role, fallback) {
  if (!map) return fallback;
  for (let rank = roleRank(role); rank >= 0; rank -= 1) {
    const key = ROLE_ORDER[rank];
    if (map[key] !== undefined) return map[key];
  }
  return fallback;
}
